/**
 * data-action Delegator
 *
 * Server-rendered markup carries no JS handlers, so behaviour attaches through
 * `data-*` attributes resolved by one document-level listener per delegated
 * event. Which events those are is NOT decided here — `VIEW_EVENT_BINDINGS` in
 * `@brave-new-world/shared` is the contract, so the emitters (`views/lib/attrs.ts`),
 * this dispatcher and `scripts/check-inline-js.mjs` all read one declaration.
 *
 * Usage:
 *   <span data-action="modal" data-modal="signup:step2:1" data-size="dynamic">Link</span>
 *   <span data-action="navigate" data-href="/dashboard">Go</span>
 *   <span data-action="call" data-fn="dofollow" data-args="1,artist,123">Follow</span>
 *   <select data-on-change="call" data-fn="toggleOption" data-use-value="true" data-args="filterid">
 *   <input  data-on-keyup="call" data-fn="searchtimed">
 *
 * (`data-use-value` prepends the element's current value to the args.)
 *
 * ⚠️ `data-args` is comma-split into STRINGS. A handler declaring `number`
 * receives a string, and an argument containing a comma silently becomes two.
 * That is the remaining untyped half of this seam — see Phase 4 of the plan.
 */

import { getAction } from "../actionRegistry";
import { tryClientNavigate } from "./clientRoute";
import {
  VIEW_EVENT_BINDINGS,
  type ViewDispatchKind,
  type ViewEventBinding,
} from "@brave-new-world/shared";
import { modal } from "../ui/modal";
import { toastSuccess } from "../ui/messages";
import { fadeOut } from "../utils/dom";

type ActionHandler = (el: HTMLElement, event: Event) => void;

/**
 * Report a dispatch that resolved to nothing.
 *
 * Every failure path here used to be a bare `return`, so a mistyped action name,
 * an unregistered `data-fn` and a handler that threw were all indistinguishable
 * from "the click did nothing". Dev-only: production users cannot act on it, and
 * the console is where the person who can is looking.
 */
function reportDeadDispatch(message: string): void {
  if (window.location.hostname === "localhost") {
    console.error(`[actionDelegator] ${message}`);
  }
}

function callFn(el: HTMLElement, event: Event) {
  // The `data-fn-<event>` per-event override that used to live here is gone. It
  // computed a dataset key by string surgery — el.dataset[`fn${Type}`] — which no
  // checker could see, nothing in either app or the legacy PHP ever emitted, and
  // which could not have worked for the `enter` alias anyway (event.type there is
  // "keydown", so it looked for `data-fn-keydown`).
  const fn = el.dataset.fn;
  if (!fn) {
    reportDeadDispatch(`<${el.tagName.toLowerCase()}> dispatches "call" with no data-fn`);
    return;
  }

  const func = getAction(fn);
  if (typeof func !== "function") {
    reportDeadDispatch(`data-fn="${fn}" is not a registered action`);
    return;
  }

  let args: unknown[] = el.dataset.args ? el.dataset.args.split(",") : [];
  if (el.dataset.useValue === "true" && event.target instanceof HTMLElement) {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    args = [target.value, ...args];
  }
  (func as (...a: unknown[]) => void)(...args);
}

/**
 * `satisfies Record<ViewDispatchKind, ActionHandler>` is the point: a kind listed
 * in the shared contract with no handler here, or a handler here that the
 * contract does not list, is a compile error. `confirm-call` used to sit in this
 * map with zero emitters anywhere — that is no longer expressible.
 */
const actions = {
  modal(el) {
    const what = el.dataset.modal ?? "";
    const size = el.dataset.size ?? "dynamic";
    const swap = el.dataset.swap ? Number(el.dataset.swap) : undefined;
    modal(what, size, swap);
  },

  navigate(el) {
    const href = el.dataset.href;
    if (!href) return;
    // A client-routed page (currently only /dashboard) may take this over and swap the
    // view instead of reloading the document. Everything else falls through to a normal
    // navigation, which is what a link on a server-rendered page should do.
    if (tryClientNavigate(href)) return;
    location.href = href;
  },

  // <select data-on-change="navigate-value" data-href="/base/"> — navigates to
  // data-href + the select's current value. Only meaningful on data-on-change:
  // it reads the event target's value, so it is never a `data-action`.
  "navigate-value"(el, event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    const href = (el.dataset.href ?? "") + target.value;
    if (tryClientNavigate(href)) return;
    location.href = href;
  },

  call: callFn,

  "scroll-top"() {
    window.scrollTo(0, 0);
  },

  open(el) {
    const href = el.dataset.href;
    if (href) window.open(href);
  },

  "hide-element"(el) {
    const target = el.dataset.target;
    if (target) {
      const elem = document.getElementById(target);
      if (elem) elem.style.display = "none";
    }
  },

  "hide-and-msg"(el) {
    const target = el.dataset.target;
    const msg = el.dataset.msg ?? "";
    if (target) {
      fadeOut(target);
    }
    if (msg) toastSuccess(msg);
  },
} satisfies Record<ViewDispatchKind, ActionHandler>;

function handleEvent(attrName: string, event: Event) {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  const el = target.closest<HTMLElement>(`[${attrName}]`);
  if (!el) return;
  const actionName = el.getAttribute(attrName);
  if (!actionName) return;

  // An own-property check, not a plain index: `actions[name]` also resolves
  // inherited keys, so `data-action="constructor"` found a callable. Harmless in
  // effect here, but it is the same lookup shape already fixed in
  // routes/reseller/dispatcher.ts — where the comment notes that
  // noUncheckedIndexedAccess does not help, because inherited keys really are
  // present on the object. `hasOwnProperty.call` rather than `Object.hasOwn`
  // because this bundle targets ES2020 (lib/sql.ts uses the same form).
  if (!Object.prototype.hasOwnProperty.call(actions, actionName)) {
    reportDeadDispatch(`${attrName}="${actionName}" has no handler`);
    return;
  }
  const handler = (actions as Record<string, ActionHandler>)[actionName];
  if (!handler) return;

  // A throwing handler used to propagate out of the document listener and become
  // an uncaught exception: no user-visible signal, and the click simply appeared
  // to do nothing. Catching here keeps one broken action from being silent, and
  // stops it aborting anything else listening on the same event.
  try {
    handler(el, event);
  } catch (err) {
    console.error(`[actionDelegator] ${attrName}="${actionName}" threw`, err);
  }
}

export function initActionDelegator(): void {
  // Widened to the interface so the loop can read the shared table generically;
  // `VIEW_EVENT_BINDINGS` is `as const` to keep `ViewEventAttr` a literal union.
  const bindings: readonly ViewEventBinding[] = VIEW_EVENT_BINDINGS;
  for (const binding of bindings) {
    document.addEventListener(binding.event, (event) => {
      handleEvent(binding.attr, event);
    });
  }
}
